import { NavigateFunction } from 'react-router-dom';
import API_CONSTANT from '../constants/APIConstant';
import { get, postJson, deleteJson, putJson, ApiResult } from './apiClient';

export interface VisitorList {
    visitors: VisitorDTO[];
    isAllDetailRequired: boolean;
    wings: string[]
}

export interface VisitorDTO {
    type: string;
    mode: string;
    visitorId: string;
    isUser: boolean;
    uId: string;
    mobile: string;
    name: string;
    photo: string;
    vehicleNumber: string;
    company?: string;
    status: string;
    source?: string;
    wingName?: string;
    homeNo?: string;
    enteredAt: number;
    exitedAt: number;
    wingId?: number;
    homeId?: number;
    isAlreadyInside?: boolean;
    isAlreadyScanned?: boolean;
    scanned?: number;
    sourceUid?: string;
    sourceSocietyName?: string;
    visitId?: number;
    approvedByName?: string;
    // Creation time (epoch ms). Absent on older backends, which put it in enteredAt.
    createdAt?: number;
    // Active resident's mobile for the visit's home; GUARD/SOCIETY_ADMIN callers only,
    // on the single-visit GET (with visitId) and the addVisitor response.
    homeContactMobile?: string;
}

export interface ResidentLookupDTO {
    uid: string;
    name: string;
    mobile: string;
    photo?: string;
    societyName: string;
    isSameSociety?: boolean;
    wingName?: string;
    homeNo?: string;
    hasResidentMembership: boolean;
    dailyHelp: {
        dailyHelpId: number;
        category: string;
        linkedHomes: { homeId: number; wingName: string; homeNo: string; checkedIn?: boolean }[];
    } | null;
}

export const getVisitorAPI = async (navigate: NavigateFunction, from?: string, to?: string, signal?: AbortSignal) => {
    let url = API_CONSTANT.PATH.VISITOR.GET;
    if (from && to) url += `?from=${from}&to=${to}`;
    const res: ApiResult<any> = await get<any>(navigate, url, { signal });
    if (res.success && res.data) return res.data;
    return { coming: [], entered: [], exited: [] };
}

export const getVisitorByIdAPI = async (navigate: NavigateFunction, visitorId: string, visitId?: number) => {
    const url = visitId
        ? `${API_CONSTANT.PATH.VISITOR.GET}?visitId=${visitId}`
        : `${API_CONSTANT.PATH.VISITOR.GET}?visitorId=${visitorId}`;
    const res: ApiResult<any> = await get<any>(navigate, url);
    if (res.success && res.data) return res.data;
    return null;
}

export const addVisitorAPI = async (navigate: NavigateFunction, visitorData: any) => {
    const formattedData = {
        ...visitorData,
        visitorImage: visitorData.visitorImage ? {
            name: visitorData.visitorImage.name,
            data: visitorData.visitorImage.data,
            type: visitorData.visitorImage.type
        } : null
    };
    const res: ApiResult<any> = await postJson<any>(navigate, API_CONSTANT.PATH.VISITOR.ADD, formattedData);
    if (res.success) return { 
        success: true, 
        message: (res.data as any)?.message || "Visitor created successfully",
        data: res.data 
    };
    return { success: false, message: res.message || "Something went wrong!" };
}

export const deleteVisitorAPI = async (navigate: NavigateFunction, visitorData: any) => {
    const res: ApiResult<any> = await deleteJson<any>(navigate, API_CONSTANT.PATH.VISITOR.DELETE, visitorData);
    if (res.success) return { success: true, message: (res.data as any)?.message };
    return { success: false, message: res.message || "Something went wrong!" };
}

// Picks the exact visit out of a GET /visitor response. The endpoint returns the list
// wrapper { visitors: [...] } (one person can have several visits), never a bare visitor.
// Preference: the visit matching visitId, else the first COMING, else the first.
export const pickVisit = (data: any, visitId?: number | null): VisitorDTO | null => {
    if (!data) return null;
    const list: VisitorDTO[] = Array.isArray(data.visitors) ? data.visitors : (data.visitorId ? [data] : []);
    if (list.length === 0) return null;
    if (visitId != null) {
        const exact = list.find(v => Number(v.visitId) === Number(visitId));
        if (exact) return exact;
    }
    return list.find(v => v.status === 'COMING') || list[0];
};

// Fetch + unwrap + pick in one step.
export const getVisitAPI = async (navigate: NavigateFunction, visitorUuid: string, visitId?: number | null): Promise<VisitorDTO | null> => {
    const data = await getVisitorByIdAPI(navigate, visitorUuid, visitId ?? undefined);
    return pickVisit(data, visitId);
};

// Human wording for a visit someone else already answered (or that can no longer be
// answered); never renders "undefined". Matches the Visitor Flows popup state 5/7 copy.
export const alreadyDecidedText = (v?: { status?: string; approvedByName?: string } | null, serverMessage?: string): string => {
    const name = v?.approvedByName;
    switch (v?.status) {
        case 'ENTERED':
        case 'APPROVED':
        case 'GUARD':
            return name ? `${name} already approved` : 'Already approved';
        case 'REJECTED': return name ? `${name} already denied` : 'Already denied';
        case 'EXITED': return 'Visitor already left';
        case 'DELETED':
        case 'CANCELLED':
            return 'This entry was cancelled';
    }
    const msg = (serverMessage || '').trim();
    const m = msg.match(/^Already (approved|rejected|denied) by (.+)$/i);
    if (m) return `${m[2]} already ${/approved/i.test(m[1]) ? 'approved' : 'denied'}`;
    if (msg) return msg;
    return 'Already updated';
};

// Any 409 from PUT /visitor: the visit moved on (someone answered, the guard cancelled,
// or the transition no longer applies). The message is the backend's human text, e.g.
// "Already approved by X", "This entry was cancelled", "Visitor already left".
export const isAlreadyDecided = (r?: { status?: number; message?: string } | null): boolean =>
    !!r && r.status === 409 && !!r.message;

export const updateVisitorAPI = async (navigate: NavigateFunction, visitorData: any) => {
    const res: ApiResult<any> = await putJson<any>(navigate, API_CONSTANT.PATH.VISITOR.UPDATE, visitorData);
    if (res.success) return { success: true, status: res.status, message: (res.data as any)?.message as string | undefined };
    return { success: false, status: res.status, message: (res.message || "Something went wrong!") as string | undefined };
}

export const scanVisitorAPI = async (navigate: NavigateFunction, visitorId: string)
    : Promise<{ data: VisitorDTO | null; error: string | null }> => {
    const res = await putJson<any>(navigate, API_CONSTANT.PATH.VISITOR.SCAN, { visitorId });
    if (res.success) return { data: res.data as VisitorDTO, error: null };
    return { data: null, error: res.message || 'Scan failed' };
};

export interface VisitorLookupResult {
    found: boolean;
    name?: string;
    mobile?: string;
    photo?: string;
    visitorUuid?: string;
}

export const lookupVisitorByMobileAPI = async (navigate: NavigateFunction, mobile: string): Promise<VisitorLookupResult> => {
    const res: ApiResult<any> = await get<any>(navigate, `${API_CONSTANT.PATH.VISITOR.LOOKUP}?mobile=${encodeURIComponent(mobile)}`);
    if (res.success && res.data) return res.data as VisitorLookupResult;
    return { found: false };
};

export const lookupResidentByUidAPI = async (navigate: NavigateFunction, uid: string)
    : Promise<{ data: ResidentLookupDTO | null; error: string | null }> => {
    const res: ApiResult<any> = await get<any>(navigate, `${API_CONSTANT.PATH.USER.LOOKUP_BY_UID}?uid=${encodeURIComponent(uid)}`);
    if (res.success && res.data) return { data: res.data as ResidentLookupDTO, error: null };
    return { data: null, error: res.message || 'Resident not found' };
};

